/**
 * Time zones (owner rulings 2026-10-28): everything AuditPlus SHOWS is US
 * Eastern with an "ET" label — one clock for everyone. A person's own zone
 * (My account → Your time zone; default Eastern) is used only to TRANSLATE
 * an hour they pick: the picker shows their time, the app saves Eastern.
 * Pure (shared with the server: no browser imports).
 */

export const COMPANY_TIME_ZONE = 'America/New_York';

export interface TimeZoneChoice {
  id: string;
  label: string;
  /** Short name shown next to a translated hour ("6 AM Central"). */
  short: string;
}

/** The US zones people can pick (DST handled by the IANA ids — Arizona and Hawaii keep no DST). */
export const TIME_ZONES: readonly TimeZoneChoice[] = [
  { id: 'America/New_York', label: 'Eastern', short: 'Eastern' },
  { id: 'America/Chicago', label: 'Central', short: 'Central' },
  { id: 'America/Denver', label: 'Mountain', short: 'Mountain' },
  { id: 'America/Phoenix', label: 'Arizona (no daylight saving)', short: 'Arizona' },
  { id: 'America/Los_Angeles', label: 'Pacific', short: 'Pacific' },
  { id: 'America/Anchorage', label: 'Alaska', short: 'Alaska' },
  { id: 'Pacific/Honolulu', label: 'Hawaii (no daylight saving)', short: 'Hawaii' },
];

/** A known zone id, or Eastern (unset / unknown = company time). */
export function knownTimeZone(id: string | null | undefined): string {
  const t = (id ?? '').trim();
  return TIME_ZONES.some((z) => z.id === t) ? t : COMPANY_TIME_ZONE;
}

export const zoneShort = (id: string) => TIME_ZONES.find((z) => z.id === id)?.short ?? 'Eastern';

/** Minutes the zone is ahead of UTC at that moment (negative in the US). */
export function utcOffsetMinutes(zone: string, at: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(at);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'));
  return Math.round((asUtc - Math.floor(at.getTime() / 60_000) * 60_000) / 60_000);
}

/** The UTC instant of midnight starting Eastern day `ymd` (YYYY-MM-DD) — daylight saving handled. */
export function easternMidnightIso(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number];
  // 05:00Z is just before or after Eastern midnight on any day, before the 2 AM change.
  const offset = utcOffsetMinutes(COMPANY_TIME_ZONE, new Date(Date.UTC(y, m - 1, d, 5)));
  return new Date(Date.UTC(y, m - 1, d) - offset * 60_000).toISOString();
}

/** YYYY-MM-DD `days` after `ymd` (negative = before) — plain calendar arithmetic. */
export function addCalendarDays(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** Whole hours Eastern is ahead of `zone` right now (Central = 1, Pacific = 3, Hawaii = 5 or 6). */
export function hoursBehindEastern(zone: string, at: Date = new Date()): number {
  return Math.round((utcOffsetMinutes(COMPANY_TIME_ZONE, at) - utcOffsetMinutes(zone, at)) / 60);
}

/**
 * The hours a person can pick, in THEIR zone, for Eastern send hours
 * first..last (inclusive) on the same day: [their hour, Eastern hour]. A
 * zone west of Eastern simply shifts earlier (Pacific 2 AM–5 PM for 5 AM–8 PM
 * ET); a pick never crosses midnight, so a weekday or day of the month
 * means the same day in both zones.
 */
export function pickableHours(zone: string, first: number, last: number, at: Date = new Date()): { local: number; eastern: number }[] {
  const behind = hoursBehindEastern(zone, at);
  const out: { local: number; eastern: number }[] = [];
  for (let eastern = first; eastern <= last; eastern++) {
    const local = eastern - behind;
    if (local >= 0 && local <= 23) out.push({ local, eastern });
  }
  return out;
}

/** "6 AM" for 6, "12 PM" for 12. */
export function hourText(h: number): string {
  if (h === 0) return '12 AM';
  if (h === 12) return '12 PM';
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
}
